import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@/components/ui/Tooltip';
import {
  refusedRegionIds,
  regionOptions as regionPickerOptions,
  retiredRegionNames,
} from '@/components/companies/company-master-options';
import {
  InlineRegionsField,
  type InlineSetSaveResult,
} from '@/components/companies/InlineRegionsField';
import { Badge } from '@/components/ui/Badge';
import { Notice } from '@/components/ui/Notice';
import { ValuePills } from '@/components/ui/ValuePills';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Modal } from '@/components/ui/Modal';
import { MultiCombobox } from '@/components/ui/MultiCombobox';
import type { ComboboxOption } from '@/components/ui/Combobox';
import { InlineField, type InlineSaveResult } from '@/components/ui/InlineField';
import type { MultiComboboxOption } from '@/components/ui/MultiCombobox';
import { ApiError, api } from '@/lib/api';
import { formatDateOnly } from '@/lib/format';
import { cityLabel } from '@/lib/use-project-options';
// Option lists live in `src/lib`, not here — this module exports only
// components, since mixing the two would defeat fast refresh.
import type { IntakeSource, PickerSource } from '@/lib/use-project-edit-options';
import type { CapacityFigures, Project, ProjectUpdateInput, ReferenceRecord } from '@/lib/types';
import {
  PROJECT_WRITE_KEYS,
  type ProjectProductSelection,
  projectProductsPayload,
  seedsLineItemsOf,
  validateProjectCapacity,
  validateProjectCityIds,
  validateProjectDate,
  validateProjectDescription,
  validateProjectName,
  validateProjectProductIds,
  capacityPlanBody,
  capacityPlanStrings,
  validateCapacityPlanField,
  type CapacityPlanKey,
} from './project-field-rules';
import { Field, Input } from '@/components/ui/Input';
import {
  CAPACITY_CATEGORIES,
  CAPACITY_FIELD_LABELS,
  CAPACITY_STATE_BADGE,
  capacityState,
  soldAgainstPlan,
  type CapacityCategory,
} from '@/lib/project-capacity';

/*
 * Eleven columns, eleven cells, one endpoint (`docs/api/projects.md`).
 * `ProjectFormModal` is the other implementation — kept for editing many
 * fields at once, while these cells are the fast path for one.
 *
 * `InlineField` owns the interaction (idle → editing → saving, Escape
 * reverts, untouched fires nothing, failed save keeps the typed value) and
 * knows nothing about the API. Each cell owns only the request key it
 * writes and what a refusal on that key means.
 *
 * Contract rules:
 *  1. ⚠️ Cycle + intake are ONE write. The server validates the pair on the
 *     post-merge record, so a lone `cycleId` re-validates the STORED intake
 *     and returns 422 on `intakeId`. `cycleBody()` always emits
 *     `{ cycleId, intakeId: null }` so a lone `cycleId` can't be sent.
 *  2. ⚠️ `startDate`/`endDate` are NOT a pair, despite rule 1's shape — the
 *     server only ever REFUSES an inverted period, never adjusts it, so
 *     each date is sent alone. `{startDate}` can come back 422 on
 *     `endDate`; see `refusalOf`'s `other` branch.
 *  3. ⚠️ `capacity` `''` (uncapped/null) and `'0'` (recorded zero) are
 *     different facts — never `Number(...) || null`, which collapses them.
 *  4. ⚠️ `cityIds` is a whole-set REPLACE: `[…]` replaces, `[]` clears,
 *     omitted leaves alone, `null` is 422. Written once on blur, never per
 *     chip or when unchanged.
 *  5. ⚠️ A picker's list isn't the record's list — `regionId`, `productId`,
 *     and `cityIds` members must be ACTIVE to write, but a read resolves
 *     retired rows fine. Offer active rows and inject the stored value back
 *     (`withStoredOption`/`pinStored`) so a retired value doesn't render
 *     blank and get silently reassigned.
 *  6. A failed picker fetch DISABLES the control rather than offering an
 *     empty dropdown.
 *
 * ## The six contract rules that shape this file
 *
 *  1. ⚠️ **The cycle and the intake are ONE write.** `cycleId` is required and
 *     `intakeId` must live inside it, and the server judges the pair on the
 *     POST-MERGE record — so a lone `cycleId` re-validates the STORED intake and
 *     comes back `422` naming `intakeId`, a field the user never touched and
 *     which has no control in the request. `ProjectCycleCell` therefore emits
 *     `{ cycleId, intakeId: null }` in ONE body, always, and the shape of
 *     `cycleBody()` is what makes a lone `cycleId` inexpressible rather than
 *     merely avoided. Same family as `{companyId, pointOfContactId}` on a
 *     deliverable and `{issueDate, validTill}` on a quote.
 *  2. ⚠️ **`startDate` and `endDate` are NOT a pair — the opposite conclusion
 *     from the same post-merge shape, and the one place in this file where
 *     copying rule 1 is wrong.** The server checks `startDate <= endDate`
 *     against the merged record too, but it only ever REFUSES, never adjusts,
 *     so a one-sided PATCH is either accepted or told exactly what is wrong.
 *     Bundling the unchanged half would re-assert a value nobody touched.
 *     `{startDate}` alone therefore comes back `422` naming `endDate` — a key
 *     the request never carried — and that is handled rather than prevented:
 *     see `refusalOf`'s `other` branch, which exists for this.
 *  3. ⚠️ **`capacity` `''` and `'0'` are different facts.** `''` is uncapped
 *     (`null`), `'0'` is a recorded capacity of none. The draft is a STRING the
 *     whole way through and only the emptiness test decides which is meant —
 *     never `Number(...) || null`, which turns "we never set a ceiling" into
 *     "sold out" silently.
 *  4. ⚠️ **`cityIds` AND `regionIds` are whole-set REPLACES.** `[…]` replaces,
 *     `[]` clears, omitted leaves alone, `null` is a `422`. So each set is
 *     written ONCE when the reader leaves the field, never per chip, and never
 *     when the membership is unchanged — re-asserting a set holding a
 *     since-retired member is a 422 on a field nobody opened.
 *
 *     ⚠️ **`regionIds` joined this rule on 2026-08-29 (migration 095).** This
 *     line named `cityIds` alone, because a project's region was then a single
 *     `regionId`. Both the old read (`region`) and the old write key are GONE:
 *     reading `project.region` yields `undefined` and renders as a blank cell,
 *     while sending `regionId` is a 422 naming the key — **the write fails
 *     loudly and the read fails silently**, which is why the blank column was
 *     the half nobody reported.
 *  5. ⚠️ **A picker's list is not the record's list.** Every `regionIds` and
 *     `cityIds` member, and `productId`, must be ACTIVE at write time, while a
 *     READ resolves retired rows happily. Both halves are needed: offer the
 *     active rows, and inject the record's own stored values back so a project
 *     on a retired row does not render blank and get silently reassigned by the
 *     next save. `pinStored` below and `regionPickerOptions` are that injection.
 *  6. **A failed picker fetch DISABLES the control.** With no list the only
 *     reachable outcome is clearing a value the reader can see, so the cell says
 *     why instead of offering an empty dropdown.
 *
 * ## Gating
 *
 * Every cell is mounted UNCONDITIONALLY and self-gates on `edit.denial`.
 * `{canUpdate && <Cell/>}` would hide the VALUE from a `viewer`, and a viewer
 * may read this page — read on `/projects` is open to every role.
 */

/* -------------------------------------------------------------------------
 * The shared write path
 * --------------------------------------------------------------------- */

/**
 * What every cell needs and nothing more, assembled once by the page.
 *
 * `denial` is `denialReason(ability, 'update', 'projects')` — `null` when the
 * reader may write. It is passed rather than recomputed per cell so eight
 * controls cannot word one refusal eight ways.
 */
export interface ProjectEditContext {
  denial: string | null;
  products: PickerSource;
  cycles: PickerSource;
  regions: { records: ReferenceRecord[]; loading: boolean; failed: boolean };
  cities: PickerSource;
  /** The active city ids, for refusing a set that still holds a retired one. */
  activeCityIds: ReadonlySet<string> | null;
  intakesFor: (cycleId: number | null) => IntakeSource;
  /**
   * Applies the SERVER's record to the loaded row.
   *
   * ⚠️ Each cell passes only the keys it owns, never the whole record — two
   * cells on one row can resolve out of order, and applying a whole record
   * last would revert whatever landed in between.
   */
  onSaved: (id: number, patch: Partial<Project>) => void;
}

/**
 * Turns a refusal into one sentence for the one control that caused it.
 *
 * ⚠️ Ordered by SPECIFICITY, not by which shape arrived first: a field error
 * naming this cell's own key first, then the form-level message, then
 * `message` last (on a 422 that's just "Validation failed").
 *
 * `writeKeys` are the keys THIS cell sends, so e.g. the cycle cell can
 * surface a refusal naming `intakeId` — it caused it and is the only
 * control that can undo it.
 */
function refusalOf(caught: unknown, writeKeys: readonly string[]): InlineSaveResult {
  if (!(caught instanceof ApiError)) {
    return {
      status: 'error',
      message: 'Could not reach the server. Check your connection and try again.',
    };
  }
  if (caught.status === 403) {
    /*
     * A 403 carries no `details`, so it can't be a per-field error. It lands
     * on the control the reader just used rather than a page banner. Roles
     * can change under a five-day access token, so this can still happen
     * even with the control already hidden below the write tier.
     */
    return {
      status: 'error',
      message: 'Your account no longer has permission to edit projects. Nothing was saved.',
    };
  }
  if (caught.status === 404) {
    return { status: 'error', message: 'This project no longer exists. Reload the list.' };
  }

  const { fields, unroutable } = caught.routeFieldErrors(PROJECT_WRITE_KEYS);
  const named = writeKeys.map((key) => fields[key]).find((message) => message !== undefined);
  // A path naming a key this cell didn't send is still the server's own
  // sentence and the most specific thing available — kept, not dropped.
  const other = Object.entries(fields)
    .filter(([key]) => !writeKeys.includes(key))
    .map(([key, message]) => (message.includes(key) ? message : `${key}: ${message}`));
  const extras = unroutable.filter((line) => line !== caught.message);

  return {
    status: 'error',
    message: named ?? other[0] ?? extras[0] ?? caught.formMessage ?? caught.message,
  };
}

/**
 * One PATCH, and the read-back that says whether it took.
 *
 * ⚠️ A 200 is not storage. `PATCH /projects/:id` is `.strict()` — an unknown
 * key is a 422, but strictness only means "recognised", not "applied". The
 * response is compared against what was asked for so a silent no-op becomes
 * a visible `warning` instead of "Saved." over a value that didn't move.
 */
async function saveField(
  row: Project,
  body: ProjectUpdateInput,
  /**
   * Keys this body carries, typed against `ProjectUpdateInput` so a
   * misspelling is a build error, not a silent fallthrough to "Validation
   * failed".
   */
  writeKeys: readonly (keyof ProjectUpdateInput)[],
  applied: (saved: Project) => boolean,
  patchOf: (saved: Project) => Partial<Project>,
  onSaved: ProjectEditContext['onSaved'],
  /**
   * Which of the values just sent the server named in its refusal — for a
   * SET-valued cell only.
   *
   * A single-valued cell has one control and one message slot, so the sentence
   * is the whole answer. A set-valued one has a chip per member and the 422
   * names **every** offender at once (`"Regions do not exist or are not
   * active: 3, 9"`), deliberately, so all the bad chips mark in one pass rather
   * than the user resubmitting to discover the next. Collapsing that into the
   * sentence alone throws the useful half away.
   *
   * Optional, so the eight single-valued cells are unchanged. The return type
   * widens to `InlineSetSaveResult`, which is `InlineSaveResult` plus one
   * optional key — assignable wherever the narrower type is expected.
   */
  refusedValues?: (caught: ApiError) => readonly string[],
): Promise<InlineSetSaveResult> {
  try {
    const { data } = await api.updateProject(row.id, body);
    // Apply regardless of outcome — the row must reflect what the record
    // holds, not the draft.
    onSaved(row.id, { ...patchOf(data), updatedAt: data.updatedAt });
    if (!applied(data)) {
      return {
        status: 'warning',
        message:
          'The server accepted the request but the value did not change. Nothing was saved — try again, and report it if it keeps happening.',
      };
    }
    return { status: 'saved' };
  } catch (caught) {
    const failure = refusalOf(caught, writeKeys);
    if (failure.status !== 'error' || !refusedValues || !(caught instanceof ApiError)) {
      return failure;
    }
    const invalidValues = refusedValues(caught);
    // An empty list is not "nothing was refused" — it is "the refusal named no
    // id we sent", which is every 403, every 404 and any 422 about something
    // else. Marking nothing is right there; adding the key would mark nothing
    // while claiming the chips had been checked.
    return invalidValues.length > 0 ? { ...failure, invalidValues } : failure;
  }
}

/**
 * The record's own stored value, injected back into an active-only list.
 *
 * ⚠️ The injection is unconditional; only the "no longer offered" REASON is
 * gated. An unread/failed list is `[]`, so labelling every stored value
 * retired on the strength of a request that never came back would be wrong.
 */
function pinStored(
  options: ComboboxOption[],
  stored: { id: number; name: string } | null,
  listIsTheActiveMaster: boolean,
): ComboboxOption[] {
  if (!stored) return options;
  const value = String(stored.id);
  if (options.some((option) => option.value === value)) return options;
  return [
    {
      value,
      label: stored.name,
      ...(listIsTheActiveMaster ? { hint: 'No longer offered' } : {}),
    },
    ...options,
  ];
}

/** Why a picker cannot be used, or `null` when it can. Permission wins. */
function pickerDenial(edit: ProjectEditContext, source: PickerSource, noun: string): string | null {
  if (edit.denial) return edit.denial;
  if (source.failed) return `${noun} could not be loaded.`;
  return null;
}

/* -------------------------------------------------------------------------
 * The cells
 * --------------------------------------------------------------------- */

interface CellProps {
  row: Project;
  edit: ProjectEditContext;
}

/**
 * The project's name, edited in place — plus the link to its detail page.
 *
 * ⚠️ The trigger is a `<button>`, so the link can't nest inside it. It's
 * moved into `InlineField`'s `action` slot instead, which renders beside the
 * trigger in both branches so a viewer keeps it too.
 */
export function ProjectNameCell({ row, edit }: CellProps) {
  return (
    <InlineField
      label="Project name"
      value={row.name}
      maxLength={240}
      validate={validateProjectName}
      disabled={edit.denial !== null}
      {...(edit.denial === null ? {} : { disabledReason: edit.denial })}
      display={
        <span className="text-sm font-medium text-fg [overflow-wrap:anywhere]">
          {row.name.trim() || `Project #${row.id}`}
        </span>
      }
      action={
        <Tooltip label={`Open ${row.name.trim() || `project #${row.id}`}`}>
          <Link
            to={`/projects/${row.id}`}
            aria-label={`Open ${row.name.trim() || `project #${row.id}`}`}
            className="mt-0.5 shrink-0 rounded-sm p-0.5 text-fg-subtle transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none"
          >
            <OpenGlyph />
          </Link>
        </Tooltip>
      }
      onSave={(next) =>
        saveField(
          row,
          { name: next },
          ['name'],
          (saved) => saved.name === next,
          (saved) => ({ name: saved.name }),
          edit.onSaved,
        )
      }
    />
  );
}

/**
 * `description` clears with `''`, not `null` — the server normalises it
 * itself, so the read-back must compare against `''` or emptying it would
 * always report a warning.
 */
export function ProjectDescriptionCell({ row, edit }: CellProps) {
  return (
    <InlineField
      label="Description"
      kind="multiline"
      rows={3}
      maxLength={16000}
      value={row.description ?? ''}
      emptyLabel="No description"
      validate={validateProjectDescription}
      disabled={edit.denial !== null}
      {...(edit.denial === null ? {} : { disabledReason: edit.denial })}
      display={
        row.description ? (
          <span className="line-clamp-2 text-xs text-fg-muted">{row.description}</span>
        ) : (
          <span className="text-xs text-fg-subtle">No description</span>
        )
      }
      onSave={(next) =>
        saveField(
          row,
          { description: next },
          ['description'],
          (saved) => (saved.description ?? '') === next,
          (saved) => ({ description: saved.description }),
          edit.onSaved,
        )
      }
    />
  );
}

/**
 * Which of the ids just sent the server named in its refusal.
 *
 * Every product key is read, not just the one this cell sends today: a save can
 * be refused at `productId` (the primary is retired) and inside `products` (a
 * member is), and either sentence names a chip on this control. Intersecting
 * with what was sent keeps a stray number in the prose from marking a chip
 * nobody chose.
 *
 * ⚠️ The `startsWith('product')` test covers `products.0.productId` as well as
 * the two flat keys, which is why it is a prefix rather than a list — a path
 * INSIDE the array still names a product the reader can see and remove.
 */
function refusedProductIds(caught: ApiError, sent: readonly string[]): string[] {
  const text = (caught.details ?? [])
    .filter((detail) => detail.path === '' || detail.path.startsWith('product'))
    .map((detail) => detail.message)
    .join(' ');
  const named = new Set(text.match(/\d+/g) ?? []);
  return sent.filter((value) => named.has(value));
}

/**
 * EVERY product this project sells — a SET since migration 156 (2026-09-09),
 * where this cell was a single-select over `productId`.
 *
 * ── WHY THIS IS ONE CONTROL AND NOT TWO ───────────────────────────────────
 *
 * The wire has three keys: `productId`, the PRIMARY, still `NOT NULL`;
 * `productIds`, the membership alone; and `products`, the membership WITH each
 * member's seeding tick. They are not three questions a reader has: the product
 * a project sells first is not a decision anybody makes on this screen, and the
 * split exists because a column could not be dropped mid-expand/contract. So
 * there is one control captioned "Products" and `projectProductsPayload` derives
 * the body from it, first chip first.
 *
 * ⚠️ **THE ASYMMETRY THIS CLOSES, because it is invisible from here.** Sending
 * `productIds` ALONE cannot remove the primary: `replaceProducts` re-asserts it
 * afterwards, so `productIds: []` means "sell only the primary" and dropping the
 * first chip would answer `200` and change nothing. The reader watches the chip
 * come back on the next load with no error anywhere. Emitting `productId` in the
 * same body moves the column first, and the stored set is then exactly the
 * chips on screen — so the misleading state is never expressible from here
 * rather than merely avoided, which is `cycleBody()`'s device one cell up.
 *
 * ⚠️ **An EMPTY selection is refused rather than sent**, and it is the only set
 * on this record that is — cities and regions both clear to nothing. A project
 * always sells at least one product, structurally, so `[]` is a request whose
 * outcome contradicts the screen that made it. The sentence is
 * `validateProjectProductIds`', shared with the form so both refuse alike.
 *
 * ── THE SEEDING TICKS, AND THE DEFECT THEY CLOSE ──────────────────────────
 *
 * 🔴 **THIS CELL SHIPPED ON 2026-09-13 SENDING `productIds`, AND THAT RESET
 * EVERY TICK TO ON.** `replaceProducts` DELETEs every link row and re-INSERTs
 * it, and a row re-inserted through `productIds` carries no `seeds_line_items`,
 * so it takes the column default — which is ON. Adding or removing ONE product
 * here therefore switched seeding back on for every product an administrator
 * had switched off in the form: a `200`, no refusal, and nothing on either
 * screen saying so. Fixed 2026-09-14 by sending `products` instead. The rule is
 * `projectProductsPayload`'s and is shared with the form, so neither surface
 * can hold half of it. **Never send `productIds` from here again.**
 *
 * The ticks are edited in `InlineRegionsField`'s `editorHeader` slot — above
 * the picker and inside `editorRef`, both load-bearing and both argued at that
 * prop. ONE save writes the membership and the ticks together, because the body
 * is built once, from one selection, at the moment the reader leaves the field.
 *
 * ⚠️ **ENTERING EDIT MODE HERE NO LONGER OPENS THE LIST — 2026-09-14, owner's
 * decision.** The popover FLIPS above the trigger when there is no room below,
 * which is over these ticks; measured at 1440 it covered 94% of them the instant
 * they appeared. So the boxes now arrive with the picker shut and the reader
 * opens it deliberately. It is gated on the SLOT rather than on this cell, so
 * the visit where `editorHeader` is absent — a record holding no products —
 * still opens eagerly, and the six mounts that pass none are untouched. The
 * measurements and the two refused alternatives are at that effect.
 *
 * ✅ **A TICK-ONLY CHANGE IS WRITTEN — `alsoDirty`, 2026-09-14.**
 * `InlineRegionsField.commit` now opens
 * `if (!alsoDirty && sameSet(next, values)) { revert(); return; }`. With the
 * `sameSet` test alone, an untouched MEMBERSHIP was read as an untouched FIELD:
 * `onSave` was never called, the draft was reverted and nothing was reported —
 * so unticking a box and changing no products lost the click behind a control
 * that answered as though it had worked. That gate is right for every other
 * caller, and was wrong only here, because this one has a SECOND kind of dirt
 * the primitive is deliberately ignorant of.
 *
 * The prop widens that ONE test. The draft, `revert`, `beginEditing` and the
 * leave detection are all untouched, and `onSave` still receives the MEMBERSHIP
 * — on a tick-only save, a set identical to `values`. Combining the two halves
 * into one body stays this cell's job, because this cell is the only thing that
 * can see both.
 *
 * ⛔ **THE TWO WORKAROUNDS IT REPLACES ARE STILL REFUSED — do not reintroduce
 * either if this ever needs revisiting.**
 *
 *   a SENTINEL id pushed into `values` so `sameSet` answers false. It corrupts
 *     the membership draft that `revert` and `beginEditing` both seed FROM
 *     `values`, and leans on private behaviour in a component this one does not
 *     own — the precise trade `editorHeader`'s own doc refuses to make.
 *
 *   a SECOND WRITE fired from the checkbox itself. Two writers of one save: it
 *     re-asserts a membership the reader may be midway through changing, and
 *     breaks the owner's rule that one Save writes the ticks and the membership
 *     together.
 *
 * ⚠️ **`alsoDirty` is a DIFF against the STORED ticks, never "the reader
 * touched a box"** — see `ticksMoved` below. Tick a box and untick it and there
 * is nothing to write: the body `projectProductsPayload` builds from an unmoved
 * set and unmoved flags is `{}`, and an empty PATCH is a 422.
 */
export function ProjectProductCell({ row, edit }: CellProps) {
  const stored = useMemo(() => row.products.map((product) => String(product.id)), [row.products]);

  /**
   * The STORED ticks, keyed by product id — the baseline half of every diff.
   *
   * ⚠️ No fallback is applied here and none is needed:
   * `ProjectProductRef.seedsLineItems` is REQUIRED on the wire, so every product
   * the server sent arrives with a real flag. `seedsLineItemsOf`'s default
   * answers for a product the reader has just ADDED, which has no stored row
   * yet, and is applied where the DRAFT is read rather than where the baseline
   * is built — folding it in here would make a missing server flag silently
   * indistinguishable from a new chip.
   */
  const storedFlags = useMemo<Record<string, boolean>>(
    () =>
      Object.fromEntries(
        row.products.map((product) => [String(product.id), product.seedsLineItems]),
      ),
    [row.products],
  );

  /**
   * ⚠️ **THE TICK DRAFT IS SPARSE, AND KEYED BY PRODUCT ID RATHER THAN BY
   * POSITION — which is what lets it survive a membership the reader is moving
   * underneath it.**
   *
   * `InlineRegionsField` owns the membership draft and does not publish it, so
   * this cell cannot watch chips appear and disappear in the popover. It does
   * not need to: nothing here enumerates this map. `projectProductsPayload`
   * walks the SELECTION's `ids` and reads each one's flag through
   * `seedsLineItemsOf`, so an entry for a product that has since been un-chosen
   * is inert rather than wrong — it is never sent, and cannot resurrect a chip.
   * The two directions of drift both settle correctly:
   *
   *   removed then saved   its entry is skipped; the link row is deleted, so
   *                        the flag had nothing left to describe.
   *   added then saved     no entry, so `SEEDS_LINE_ITEMS_DEFAULT` answers — ON,
   *                        identical to the form and to the column default, so
   *                        one rule covers every route a product arrives by.
   *   removed then re-added its entry survived the round trip, so a slip of the
   *                        hand does not quietly reset somebody's decision.
   *
   * Cleared only on a save the server applied, never on a refusal: a failed
   * write must keep the reader's ticks exactly as they assembled them, the same
   * promise `InlineRegionsField` makes about the chips beside them.
   */
  const [tickChanges, setTickChanges] = useState<Record<string, boolean>>({});
  const draftFlags = useMemo<Record<string, boolean>>(
    () => ({ ...storedFlags, ...tickChanges }),
    [storedFlags, tickChanges],
  );

  /**
   * Is there a TICK to write even though the membership has not moved? — the
   * `alsoDirty` half of the commit gate, without which this cell's edit is
   * reverted with nothing reported. See the header.
   *
   * ⚠️ **Asked OF `projectProductsPayload` rather than answered here, and that
   * is the point.** The same `ids` on both sides makes the membership diff
   * empty by construction, so `products` comes back if and only if a FLAG
   * moved — which means the predicate is the very function that builds the
   * body, and the two cannot disagree. A comparison written out here would be a
   * second copy of the rule deciding what a customer's documents get seeded
   * with: it would agree on the day it was written and drift on the first
   * correction, silently, because nothing renders a payload the caller decided
   * not to send.
   *
   * ⚠️ **`stored`, not the editor's draft membership — which this cell cannot
   * see, and does not need to.** The boxes are drawn from `row.products`, so
   * `tickChanges` can only ever key a product the record already holds, and a
   * membership change is dirt the `sameSet` test catches on its own.
   *
   * ⚠️ **A DIFF, so ticking a box and unticking it answers `false` again** and
   * the field reverts with no request. That is required rather than tidy: with
   * the set unmoved too, the body here is `{}`, and an empty PATCH is a 422 —
   * a refusal on a visit in which the reader changed their mind back.
   */
  const ticksMoved = useMemo(
    () =>
      projectProductsPayload(
        { ids: stored, seedsLineItems: draftFlags },
        { ids: stored, seedsLineItems: storedFlags },
      ).products !== undefined,
    [stored, draftFlags, storedFlags],
  );

  /*
   * Active list plus every product this record already holds, so a retired
   * member reads as a name and not a bare id. The "Retired" hint is gated the
   * way `pinStored` gates its own — an unread list makes everything look
   * retired — while the merge itself is unconditional.
   */
  const options = useMemo<MultiComboboxOption[]>(() => {
    const merged: MultiComboboxOption[] = [...edit.products.options];
    const known = new Set(merged.map((option) => option.value));
    const listIsTheActiveMaster = !edit.products.loading && !edit.products.failed;
    for (const product of row.products) {
      const value = String(product.id);
      if (known.has(value)) continue;
      known.add(value);
      merged.push({
        value,
        label: product.name,
        ...(listIsTheActiveMaster ? { hint: 'Retired' } : {}),
      });
    }
    return merged;
  }, [edit.products.options, edit.products.loading, edit.products.failed, row.products]);

  /**
   * The ids a write may legally contain, or `null` when nobody knows.
   *
   * ⚠️ `null` and an empty Set are different facts. An unread or failed list is
   * `null` and narrows NOTHING — turning a request that never came back into a
   * refusal naming the reader's live products as retired would be an accusation
   * manufactured out of a missing response. The control is disabled in that
   * state anyway, so this is belt and braces, exactly like `activeCityIds`.
   */
  const activeProductIds = useMemo<ReadonlySet<string> | null>(() => {
    if (edit.products.loading || edit.products.failed) return null;
    return new Set(edit.products.options.map((option) => option.value));
  }, [edit.products.loading, edit.products.failed, edit.products.options]);

  const denial = pickerDenial(edit, edit.products, 'Products');

  const [editing, setEditing] = useState(false);
  /**
   * The membership draft, which `InlineRegionsField` used to own.
   *
   * ⚠️ **Seeded when the dialog OPENS, not from an effect keyed on `stored`.**
   * An effect would re-seed mid-edit the moment another save landed on this row
   * and quietly discard what the reader had chosen; opening is the only moment
   * at which the stored value is the right answer.
   */
  const [draftIds, setDraftIds] = useState<string[]>(stored);
  const [saving, setSaving] = useState(false);
  /**
   * ⚠️ **`invalidValues` is carried even though only its LENGTH is read today.**
   * It is what marks the offending chips when the picker can express that, and
   * dropping it here would mean the retired-product refusal knows which ids it
   * refused and throws the list away — the server names them, and a refusal
   * that cannot point at them makes the reader hunt.
   */
  const [editorError, setEditorError] = useState<{
    message: string;
    invalidValues: readonly string[];
  } | null>(null);

  /*
   * One tick per product the RECORD holds, in the record's own order — the same
   * order as the chips, so the two are read together rather than cross-
   * referenced. The form draws its boxes from the live selection; this one
   * cannot, because the membership draft belongs to `InlineRegionsField`. The
   * difference is only in which products have a box, and both ends of it are
   * already answered: a chip added in the popover has no box and is ticked by
   * default, which is what it would be anyway; a chip removed keeps a box whose
   * flag is then skipped, because the link row it described is being deleted.
   *
   * ⛔ **There is no helper text here and none may be added.** The group's
   * LEGEND is the ticks' label — every box in the group is announced with it,
   * and a checkbox's label is what the checkbox MEANS, not a sentence sitting
   * under a control. Its wording is byte-identical to `ProjectFormModal`'s
   * deliberately: two spellings of one consequence on two screens is how they
   * drift, and this one prints on a document a customer is billed against.
   *
   * ⚠️ **ABSENT, not empty, when the record holds nothing** — a legend
   * promising a consequence over no boxes at all reads as a list that failed to
   * load. The form does the same. Unreachable on a saved record (a project
   * always sells its primary), which is exactly why it is cheap to get right.
   */
  const tickBoxes =
    row.products.length === 0 ? null : (
      <fieldset className="min-w-0">
        <legend className="mb-1.5 text-xs font-medium text-fg-secondary">
          Added as a line item on a new quote, invoice or deal
        </legend>
        <div className="grid min-w-0 gap-2 sm:grid-cols-2">
          {row.products.map((product) => (
            <Checkbox
              key={product.id}
              checked={seedsLineItemsOf(draftFlags, String(product.id))}
              onChange={(event) => {
                const { checked } = event.target;
                setTickChanges((previous) => ({ ...previous, [String(product.id)]: checked }));
              }}
              // The product's own name IS this box's label, so the legend and
              // the box read as one sentence. No `aria-label`: it would REPLACE
              // the visible text rather than add to it.
              label={product.name}
            />
          ))}
        </div>
      </fieldset>
    );

  /**
   * 🔴 **THE EDITOR IS A MODAL SINCE 2026-09-18 — owner's instruction, from a
   * screenshot of the inline one.**
   *
   * ⚠️ *SUPERSEDED: the whole editor rendered through `InlineRegionsField` in
   * the cell, with the ticks in its `editorHeader` slot.* It was correct and it
   * was unreadable: the Products column is ~200px, and **`sm:grid-cols-2` is a
   * VIEWPORT breakpoint, not a container one** — so at 1440 the tick grid split
   * a 200px cell into two ~90px columns and set every product name one word per
   * line. A product name here is *"Sales - Business School Festivals:
   * Add-ons"*, and the control that decides what lands on a customer's invoice
   * was a column of single words.
   *
   * ⛔ **AND `InlineRegionsField` COULD NOT SIMPLY BE PUT IN A MODAL.** Its
   * commit is LEAVE-BASED — document-level `focusout` and `pointerdown`
   * listeners, with a hint reading *"Leave the field to save"* — and a modal is
   * a FOCUS TRAP. Inside one, "leaving the field" never means what it means in
   * a table, and Escape would have to both revert the draft and close the
   * dialog. Two commit models in one control is worse than either.
   *
   * ✅ **SO THE RULES ARE SHARED AND THE MACHINERY IS NOT.** Everything that
   * decides what reaches the server still comes from one place —
   * `projectProductsPayload`, `validateProjectProductIds`, `refusedProductIds`,
   * `saveField` and the read-back below are untouched and are the same code the
   * form uses. What is local is the commit gesture: an explicit Save, which is
   * what a modal affords.
   */
  const dirty = ticksMoved || draftIds.length !== stored.length || draftIds.some((id, index) => id !== stored[index]);

  async function handleSave(): Promise<void> {
    const next = draftIds;

    const unusable = validateProjectProductIds(next);
    if (unusable) {
      setEditorError({ message: unusable, invalidValues: [] });
      return;
    }

    if (activeProductIds) {
      const retired = next.filter((id) => !activeProductIds.has(id));
      if (retired.length > 0) {
        const names = retired
          .map((id) => options.find((option) => option.value === id)?.label ?? id)
          .join(', ');
        setEditorError({
          message: `${names} ${retired.length === 1 ? 'is' : 'are'} retired — remove ${retired.length === 1 ? 'it' : 'them'} to save this change.`,
          invalidValues: retired,
        });
        return;
      }
    }

        /*
         * The membership comes from the editor's own draft and the ticks from
         * this cell's, combined HERE and nowhere else — one selection, one body,
         * one PATCH. A product in `next` with no tick entry takes
         * `SEEDS_LINE_ITEMS_DEFAULT` inside `seedsLineItemsOf`; nothing in this
         * file applies that default itself.
         */
    const selection: ProjectProductSelection = { ids: next, seedsLineItems: draftFlags };
    const baseline: ProjectProductSelection = { ids: stored, seedsLineItems: storedFlags };

    setSaving(true);
    try {
      const outcome = await saveField(
        row,
          // `productId` plus `products` — NEVER `productIds`, which resets every
          // tick to ON. The exclusivity is structural inside
          // `projectProductsPayload`; see this cell's header for what it cost.
          projectProductsPayload(selection, baseline),
          /*
           * `productIds` is deliberately absent: no path through
           * `projectProductsPayload` emits it, so routing a refusal naming it
           * onto this control would be dead code claiming to be a guard. A
           * server that named it anyway is still surfaced verbatim — `refusalOf`
           * keeps every field error outside `writeKeys` in its `other` branch.
           */
          ['productId', 'products'],
          (saved) => {
            // The read comes back primary-first then alphabetically, never in
            // the order it was sent — so membership is compared as a set and the
            // PRIMARY positionally, which is the only part of the order the
            // server promises and the only part this cell can move.
            const now = new Map(
              saved.products.map((product) => [String(product.id), product.seedsLineItems]),
            );
            const sameMembers = now.size === next.length && next.every((id) => now.has(id));
            const samePrimary = String(saved.products[0]?.id ?? '') === next[0];
            /*
             * ⚠️ **THE TICKS ARE READ BACK TOO, and this is the assertion the
             * 2026-09-13 defect needed.** That write was a clean `200` carrying
             * the right membership and the wrong flags, so a check on membership
             * alone reported "Saved." over every tick the request had just
             * reset. A 200 is not storage, and the half that was not checked is
             * exactly the half that moved.
             */
            const sameTicks = next.every(
              (id) => now.get(id) === seedsLineItemsOf(selection.seedsLineItems, id),
            );
            return sameMembers && samePrimary && sameTicks;
          },
          // `product` rides along so the loaded row stays coherent for the
          // surfaces still reading the deprecated key — this cell does not read
          // it, but it must not leave a stale one behind either.
          //
          // ⚠️ THE REFERENT MOVED — re-measured 2026-09-20. "The surfaces still
          // reading it" meant `ProjectDetailPage` when this was written; that
          // page now reads `project.products` throughout and greps ZERO for
          // `.product`. The only remaining readers are the two fallbacks in
          // `@/lib/use-line-project` (`productHasProjectsCache` and
          // `projectSellsProduct`), and those are a DELIBERATE guard against a
          // server predating migration 156 — see that function's own doc before
          // concluding this carry-along has nothing left to serve.
          //
          // ⛔ The behaviour is unchanged either way: what this line owes is
          // agreement with the WIRE SHAPE, which still carries the key, not
          // agreement with whoever happens to consume it this week.
          (saved) => ({ products: saved.products, product: saved.product }),
          edit.onSaved,
          (caught) => refusedProductIds(caught, next),
        );

        /*
         * Only on a write the server APPLIED. `row.products` now carries the
         * stored flags, so the draft has nothing left to say; keeping it would
         * re-assert a decision the record already holds. A `warning` is a 200
         * that changed nothing and an `error` changed nothing either — both keep
         * the reader's ticks, because on both the editor stays open in front of
         * them or the value they chose is still the one they meant.
         *
         * ⚠️ **Clearing is REQUIRED, not hygiene.** An entry left behind keeps
         * overriding `storedFlags` for as long as the row lives, so a tick
         * changed afterwards ANYWHERE ELSE — the form, another session — would
         * read back correctly from the server and still render with this cell's
         * spent answer on top of it.
         *
         * ⚠️ **Known and accepted: a box toggled DURING the in-flight save is
         * discarded by this line.** The body and the read-back both closed over
         * `selection` when the request was built, so the write itself stays
         * coherent; what is lost is a decision made in the sub-second the editor
         * is still on screen. It is not silent — the editor closes and the chip
         * beside it renders the stored answer, which is the one thing this cell
         * marks. Closing it properly means knowing the phase, which the
         * primitive does not publish.
         */
      if (outcome.status === 'saved') {
        setTickChanges({});
        setEditorError(null);
        setEditing(false);
        return;
      }

      /*
       * ⚠️ **A `warning` STAYS OPEN, exactly as it did inline.** It is a 200
       * that changed nothing, so the reader's draft is still what they meant
       * and closing over it would report success for a write that did not
       * land. Only a save the server APPLIED clears the ticks and the dialog.
       */
      setEditorError({
        message: outcome.message ?? 'That did not save.',
        invalidValues: outcome.invalidValues ?? [],
      });
    } finally {
      setSaving(false);
    }
  }

  const trigger = (
    <button
      type="button"
      // ⚠️ The chips ARE the label. A visually-hidden name would replace them
      // rather than add to them, so the record's own products are what a screen
      // reader announces, and the project name comes from the row.
      className="block w-full min-w-0 rounded text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
      onClick={() => {
        // Seeded HERE rather than in an effect: the draft is the stored value
        // every time the dialog opens, so a cancelled edit cannot survive into
        // the next one. Clearing the ticks matters for the same reason —
        // `draftFlags` reads `storedFlags` through `seedsLineItemsOf` once this
        // is empty.
        setDraftIds(stored);
        setTickChanges({});
        setEditorError(null);
        setEditing(true);
      }}
    >
      <ProductChips products={row.products} />
    </button>
  );

  return (
    <>
      {denial === null ? trigger : <ProductChips products={row.products} />}
      {denial !== null && <p className="mt-1 text-xs text-fg-muted">{denial}</p>}

      {editing && (
        <Modal
          open
          // ⚠️ `lg`, not `xl`. The content is a column of fields and a tick
          // grid, not a table — `Modal`'s own note reserves `xl` for tables,
          // and the whole reason this moved here is that the ticks need real
          // width, which `lg` gives them at two honest columns.
          size="lg"
          title="Products"
          onClose={() => setEditing(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
              {/*
                * ⚠️ **Disabled on an unchanged selection**, because a PATCH
                * carrying neither key is a 422 — the same reason the inline
                * editor refused to commit an untouched field. `dirty` is a DIFF
                * against what is stored and never a "they touched something"
                * latch, so ticking a box and unticking it again correctly
                * leaves nothing to save.
                */}
              <Button onClick={() => void handleSave()} loading={saving} disabled={!dirty}>
                Save products
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {editorError !== null && (
              <Notice tone="danger">{editorError.message}</Notice>
            )}

            <MultiCombobox
              values={draftIds}
              onChange={setDraftIds}
              options={options}
              loading={edit.products.loading}
              placeholder="No products chosen"
              searchPlaceholder="Filter products…"
              invalid={(editorError?.invalidValues.length ?? 0) > 0}
            />

            {tickBoxes}
          </div>
        </Modal>
      )}
    </>
  );
}

/**
 * ⚠️ THE LOAD-BEARING CELL — changing the cycle clears the intake, in the
 * same request (contract rule 1 above). `cycleBody()` always emits both
 * keys in one object literal, so a lone `cycleId` can't be produced here.
 */
function cycleBody(cycleId: number): ProjectUpdateInput {
  return { cycleId, intakeId: null };
}

export function ProjectCycleCell({ row, edit }: CellProps) {
  const options = pinStored(edit.cycles.options, row.cycle, !edit.cycles.loading && !edit.cycles.failed);
  const denial = pickerDenial(edit, edit.cycles, 'Cycles');

  return (
    <InlineField
      label="Cycle"
      kind="picker"
      value={row.cycle ? String(row.cycle.id) : ''}
      options={options}
      optionsLoading={edit.cycles.loading}
      placeholder="Select a cycle…"
      searchPlaceholder="Filter cycles…"
      disabled={denial !== null}
      {...(denial === null ? {} : { disabledReason: denial })}
      display={
        row.cycle ? (
          <span className="text-sm text-fg-secondary">{row.cycle.name}</span>
        ) : (
          <Badge tone="warning">Cycle unavailable</Badge>
        )
      }
      onSave={(next) => {
        if (next === '') {
          return Promise.resolve<InlineSaveResult>({
            status: 'error',
            message: 'Every project belongs to a cycle. Choose one rather than clearing it.',
          });
        }
        const body = cycleBody(Number(next));
        return saveField(
          row,
          body,
          // Both keys, so a refusal naming `intakeId` lands on the control that
          // caused it rather than on the intake picker the reader never opened.
          ['cycleId', 'intakeId'],
          (saved) => String(saved.cycle?.id ?? '') === next && saved.intake === null,
          (saved) => ({ cycle: saved.cycle, intake: saved.intake }),
          edit.onSaved,
        );
      }}
    />
  );
}

/**
 * The intake, scoped to the row's OWN cycle, so an intake from another cycle
 * is never offered and the cycle/intake mismatch can't be reached here.
 * `''` is a real answer (`intakeId: null`, "the whole cycle"), which is why
 * the empty row is NAMED rather than blank.
 */
export function ProjectIntakeCell({ row, edit }: CellProps) {
  const cycleId = row.cycle?.id ?? null;
  const source = edit.intakesFor(cycleId);

  const denial = (() => {
    if (edit.denial) return edit.denial;
    if (cycleId === null) return 'This project’s cycle could not be read, so its intakes cannot be listed.';
    if (source.skipped) {
      return 'Too many cycles on this page — use Edit to change this one.';
    }
    if (source.failed) return 'Intakes for this cycle could not be loaded.';
    if (!source.loading && source.options.length === 0) {
      // Not a failure: a cycle with no intakes means the project spans it whole,
      // and there is genuinely nothing to choose.
      return 'This cycle has no intakes — the project spans the whole cycle.';
    }
    return null;
  })();

  return (
    <InlineField
      label="Intake"
      kind="picker"
      value={row.intake ? String(row.intake.id) : ''}
      options={source.options}
      optionsLoading={source.loading}
      placeholder="The whole cycle — no tracking"
      emptyOptionLabel="The whole cycle — no tracking"
      searchPlaceholder="Filter intakes…"
      emptyLabel="Whole cycle"
      disabled={denial !== null}
      {...(denial === null ? {} : { disabledReason: denial })}
      display={
        <span className="text-xs text-fg-muted">{row.intake ? row.intake.name : 'Whole cycle'}</span>
      }
      onSave={(next) =>
        saveField(
          row,
          { intakeId: next === '' ? null : Number(next) },
          ['intakeId'],
          (saved) => String(saved.intake?.id ?? '') === next,
          (saved) => ({ intake: saved.intake }),
          edit.onSaved,
        )
      }
    />
  );
}

/* -------------------------------------------------------------------------
 * The period: two dates, two independent writes (contract rule 2 above).
 * Each cell sends its own key alone; a refusal naming the other date is
 * surfaced verbatim by `refusalOf`'s `other` branch.
 *
 * ⚠️ Neither date is checked against the cycle's window, here or anywhere —
 * a project may legitimately run outside it, and the API never refuses for
 * it. A client-side check would make a legal project unsaveable silently.
 * --------------------------------------------------------------------- */

/**
 * `null` is ordinary, not a gap — the two date columns are independently
 * nullable and all four combinations are legal.
 *
 * ⚠️ Uses `row.startDate ?? ''`, NOT `toDateInputValue`, which splits an ISO
 * timestamp at `T`. This column has no time component, and using that
 * helper here would wrongly suggest it does — `new Date('2026-05-31')`
 * renders 30 May.
 */
export function ProjectStartDateCell({ row, edit }: CellProps) {
  return (
    <InlineField
      label="Start date"
      type="date"
      value={row.startDate ?? ''}
      emptyLabel="Not set"
      // `''` is indistinguishable from a part-typed date, but harmless here —
      // `''` IS a clear on this column. `validate` still catches 2026-02-30
      // before the round trip.
      validate={validateProjectDate}
      disabled={edit.denial !== null}
      {...(edit.denial === null ? {} : { disabledReason: edit.denial })}
      display={
        row.startDate === null ? (
          <span className="text-xs text-fg-subtle" title="No start date recorded">
            Not set
          </span>
        ) : (
          <span className="text-sm text-fg-secondary tabular">{formatDateOnly(row.startDate)}</span>
        )
      }
      onSave={(next) => {
        /*
         * ⚠️ **`null` to clear, never `''` — even though both work HERE.** The
         * server normalises an empty string on these two columns and refuses it
         * with a 422 on `intakeId`, `cycleId`, `productId` and `capacity`, on
         * this same endpoint. (⚠️ This list named `regionId` first until
         * 2026-08-29: that key is gone with migration 095 and is now a 422 for a
         * different reason — an unrecognised key, whatever value it carries.) A
         * codebase that learns "an empty
         * box sends an empty string" from the one field where it is tolerated
         * carries the habit to the five where it is a refusal, so the looser
         * half is never used.
         */
        const startDate = next === '' ? null : next;
        return saveField(
          row,
          { startDate },
          // Own key only — a refusal naming `endDate` is left to
          // `refusalOf`'s `other` branch; listing it here would wrongly
          // claim this control can fix it.
          ['startDate'],
          (saved) => saved.startDate === startDate,
          (saved) => ({ startDate: saved.startDate }),
          edit.onSaved,
        );
      }}
    />
  );
}

/**
 * When the project ends — the same shape as the start, and the field a
 * post-merge refusal always lands on, whichever of the two was written.
 */
export function ProjectEndDateCell({ row, edit }: CellProps) {
  return (
    <InlineField
      label="End date"
      type="date"
      value={row.endDate ?? ''}
      emptyLabel="Not set"
      validate={validateProjectDate}
      disabled={edit.denial !== null}
      {...(edit.denial === null ? {} : { disabledReason: edit.denial })}
      display={
        row.endDate === null ? (
          <span className="text-xs text-fg-subtle" title="No end date recorded">
            Not set
          </span>
        ) : (
          <span className="text-sm text-fg-secondary tabular">{formatDateOnly(row.endDate)}</span>
        )
      }
      onSave={(next) => {
        // `null` clears, `''` does not travel — see the start-date cell.
        const endDate = next === '' ? null : next;
        return saveField(
          row,
          { endDate },
          // Own key — a refusal here names this field directly, no `other`
          // branch needed.
          ['endDate'],
          (saved) => saved.endDate === endDate,
          (saved) => ({ endDate: saved.endDate }),
          edit.onSaved,
        );
      }}
    />
  );
}

/**
 * ⚠️ `''` (uncapped) and `'0'` (recorded zero) survive only because the
 * draft never goes through `Number()` — see contract rule 3. `capacity ?? 0`
 * or `Number(capacity) || null` would silently collapse them.
 *
 * The server enforces no capacity anywhere — no counter, no remaining-seats
 * field, no 409 — noted once in the table's footnote, not per cell.
 */
export function ProjectCapacityCell({ row, edit }: CellProps) {
  const value = row.capacity === null ? '' : String(row.capacity);

  return (
    <InlineField
      label="Capacity"
      value={value}
      inputMode="numeric"
      maxLength={10}
      placeholder="Uncapped"
      emptyLabel="Uncapped"
      validate={validateProjectCapacity}
      disabled={edit.denial !== null}
      {...(edit.denial === null ? {} : { disabledReason: edit.denial })}
      display={
        row.capacity === null ? (
          <span className="text-xs text-fg-subtle" title="No capacity recorded">
            Uncapped
          </span>
        ) : (
          <span
            className="text-sm text-fg-secondary tabular"
            title={row.capacity === 0 ? 'A recorded capacity of zero' : undefined}
          >
            {row.capacity}
          </span>
        )
      }
      onSave={(next) => {
        const capacity = next === '' ? null : Number(next);
        return saveField(
          row,
          { capacity },
          ['capacity'],
          (saved) => saved.capacity === capacity,
          (saved) => ({ capacity: saved.capacity }),
          edit.onSaved,
        );
      }}
    />
  );
}

/** The two write keys each category owns. */
const CATEGORY_KEYS: Record<CapacityCategory, { capacity: CapacityPlanKey; target: CapacityPlanKey }> = {
  institution: { capacity: 'institutionCapacity', target: 'institutionTarget' },
  partner: { capacity: 'partnerCapacity', target: 'partnerTarget' },
};

/**
 * Sold against plan for one category, plus the state Badge. `sold` absent
 * (an older API) reads as `—` rather than 0 — not reported is not none.
 */
function CategoryCapacityDisplay({
  sold,
  figures,
}: {
  sold: number | undefined;
  figures: CapacityFigures;
}) {
  if (sold === undefined) {
    const parts = [
      figures.capacity === null ? '—' : `— of ${figures.capacity.toLocaleString('en-US')}`,
      ...(figures.target === null ? [] : [`target ${figures.target.toLocaleString('en-US')}`]),
    ];
    return <span className="text-sm text-fg-secondary tabular">{parts.join(' · ')}</span>;
  }
  const badge = CAPACITY_STATE_BADGE[capacityState(sold, figures)];
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
      <span className="text-sm text-fg-secondary tabular">{soldAgainstPlan(sold, figures)}</span>
      {badge && <Badge tone={badge.tone}>{badge.label}</Badge>}
    </span>
  );
}

/**
 * Institutions or Partners: sold against planned capacity and target, with the
 * two figures edited together in a small dialog (owner 2026-10-27).
 *
 * ⚠️ `''` clears to `null` (not set) and `'0'` is a recorded zero — the same
 * string rule as `ProjectCapacityCell`, through `capacityPlanBody`. The body
 * carries only the keys that moved, and never `capacity` (a 422 beside them).
 *
 * ⚠️ `capacityPlan` undefined is an OLDER API: Institutions falls back to
 * today's `ProjectCapacityCell` (the legacy `capacity` is what migration 324
 * moves into Institution planned capacity) and Partners reads `—`.
 */
export function ProjectCategoryCapacityCell({
  row,
  edit,
  category,
}: CellProps & { category: CapacityCategory }) {
  const plan = row.capacityPlan;
  const label = CAPACITY_CATEGORIES.find((c) => c.key === category)!.label;
  const keys = CATEGORY_KEYS[category];

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ capacity: '', target: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (plan === undefined) {
    return category === 'institution' ? (
      <ProjectCapacityCell row={row} edit={edit} />
    ) : (
      <span className="text-sm text-fg-subtle">—</span>
    );
  }

  const figures = plan[category];
  const baseline = capacityPlanStrings(row);
  const display = <CategoryCapacityDisplay sold={row.sold?.[category]} figures={figures} />;

  const capacityError = validateCapacityPlanField(keys.capacity, draft.capacity);
  const targetError = validateCapacityPlanField(keys.target, draft.target);
  const dirty = draft.capacity !== baseline[keys.capacity] || draft.target !== baseline[keys.target];

  async function handleSave(): Promise<void> {
    if (capacityError || targetError) return;
    const body: ProjectUpdateInput = capacityPlanBody(
      { ...baseline, [keys.capacity]: draft.capacity, [keys.target]: draft.target },
      baseline,
    );
    const expected: CapacityFigures = {
      capacity: draft.capacity === '' ? null : Number(draft.capacity),
      target: draft.target === '' ? null : Number(draft.target),
    };
    setSaving(true);
    try {
      const outcome = await saveField(
        row,
        body,
        [keys.capacity, keys.target],
        (saved) =>
          saved.capacityPlan?.[category].capacity === expected.capacity &&
          saved.capacityPlan?.[category].target === expected.target,
        (saved) => ({ capacity: saved.capacity, capacityPlan: saved.capacityPlan, sold: saved.sold }),
        edit.onSaved,
      );
      if (outcome.status === 'saved') {
        setError(null);
        setEditing(false);
        return;
      }
      setError(outcome.message ?? 'That did not save.');
    } finally {
      setSaving(false);
    }
  }

  if (edit.denial !== null) {
    return (
      <Tooltip label={edit.denial}>
        <span className="inline-flex min-w-0">{display}</span>
      </Tooltip>
    );
  }

  return (
    <>
      <button
        type="button"
        className="block w-full min-w-0 rounded text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
        onClick={() => {
          setDraft({ capacity: baseline[keys.capacity], target: baseline[keys.target] });
          setError(null);
          setEditing(true);
        }}
      >
        <span className="sr-only">{label}: </span>
        {display}
      </button>

      {editing && (
        <Modal
          open
          size="sm"
          title={label}
          onClose={() => setEditing(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleSave()}
                loading={saving}
                disabled={!dirty || capacityError !== null || targetError !== null}
              >
                Save
              </Button>
            </>
          }
        >
          <div
            className="space-y-4"
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || saving || !dirty) return;
              event.preventDefault();
              void handleSave();
            }}
          >
            {error !== null && <Notice tone="danger">{error}</Notice>}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={CAPACITY_FIELD_LABELS[keys.capacity]} error={capacityError}>
                {(field) => (
                  <Input
                    {...field}
                    inputMode="numeric"
                    maxLength={10}
                    value={draft.capacity}
                    invalid={capacityError !== null}
                    onChange={(event) => {
                      const { value } = event.target;
                      setDraft((previous) => ({ ...previous, capacity: value.trim() }));
                    }}
                  />
                )}
              </Field>
              <Field label={CAPACITY_FIELD_LABELS[keys.target]} error={targetError}>
                {(field) => (
                  <Input
                    {...field}
                    inputMode="numeric"
                    maxLength={10}
                    value={draft.target}
                    invalid={targetError !== null}
                    onChange={(event) => {
                      const { value } = event.target;
                      setDraft((previous) => ({ ...previous, target: value.trim() }));
                    }}
                  />
                )}
              </Field>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

/**
 * The derived total (institution + partner planned), read-only since
 * 2026-10-27. `null` = neither planned capacity is set.
 */
export function ProjectTotalCapacityCell({ row }: { row: Project }) {
  return row.capacity === null ? (
    <span className="text-sm text-fg-subtle">—</span>
  ) : (
    <span className="text-sm text-fg-secondary tabular">{row.capacity.toLocaleString('en-US')}</span>
  );
}

/**
 * SEED's regions of focus — the project's own, never derived from a city or a
 * country. `Country.regionId` is upstream geography on an unrelated id sequence
 * and the join returns Egypt as "South East Asia": quietly wrong, which is worse
 * than obviously wrong.
 *
 * ⚠️ **A SET since migration 095 (deployed 2026-08-29), and this cell used to be
 * a single-valued `InlineField kind="picker"`** reading `row.region` and writing
 * `{ regionId }`. Quoting what stood here: *"`withStoredOption` supplies both
 * halves of the deactivation trap — active rows only, with the record's own
 * stored region injected back and labelled."* The mechanism survives the change;
 * the field it described does not. `region` and `regionId` are both gone —
 * reading the first yields `undefined` and rendered a blank cell on every
 * project, and sending the second is a 422 naming the key.
 *
 * `InlineRegionsField` is reused, not forked, for the same reason
 * `ProjectCitiesCell` reuses it: it owns the portalled-popover leave detection,
 * which is the one part nobody would get right twice. `regionPickerOptions`,
 * `retiredRegionNames` and `refusedRegionIds` are the company surface's, and the
 * backend shaped this field to match that one deliberately — a project's
 * `regionIds` and a company's are the same contract, so a second set of helpers
 * would be two implementations of one rule.
 *
 * The retired-region trap is closed exactly as it is on companies, and it takes
 * all three parts: the options list INJECTS every stored id the active master no
 * longer lists, so a retired region has a chip and cannot be dropped by a save
 * of something else; the field seeds its draft from those ids and sends the
 * draft; and `note` says, before any save is attempted, that the whole field is
 * unsaveable until the chip is removed — because `regionIds` is validated as a
 * set and one dead member refuses the lot.
 */
export function ProjectRegionCell({ row, edit }: CellProps) {
  const stored = useMemo(() => row.regions.map((region) => String(region.id)), [row.regions]);
  const options = useMemo(
    () => regionPickerOptions(edit.regions.records, row.regions),
    [edit.regions.records, row.regions],
  );
  const retired = retiredRegionNames(edit.regions.records, row.regions);

  const denial = (() => {
    if (edit.denial) return edit.denial;
    if (edit.regions.failed) {
      return 'Regions could not be loaded.';
    }
    return null;
  })();

  /*
   * The permission or load refusal wins the one `note` slot: it says the field
   * cannot be used at all, which subsumes a warning about one chip inside it.
   * `??` between two messages is a silent drop wherever both can be true and
   * both need showing — here the second is unreachable advice while the first
   * holds, so the choice is made explicitly rather than by coalescing.
   */
  const note =
    denial ??
    (retired.length > 0
      ? `${retired.join(', ')} ${
          retired.length === 1 ? 'is' : 'are'
        } retired — remove ${retired.length === 1 ? 'it' : 'them'} to save this field.`
      : null);

  return (
    <InlineRegionsField
      label="Regions of focus"
      values={stored}
      options={options}
      optionsLoading={edit.regions.loading}
      disabled={denial !== null}
      // `[]` is a real, ordinary state — nobody has classified this project —
      // and not the same fact as a single region having been cleared.
      emptyLabel="Unclassified"
      placeholder="Unclassified"
      searchPlaceholder="Filter regions…"
      {...(note === null ? {} : { note })}
      onSave={(next) =>
        saveField(
          row,
          // The WHOLE set. `[]` clears it, which is a real instruction here —
          // omitting the key is how "leave it alone" is said, and this cell
          // never omits it because it is only called when the set moved.
          { regionIds: next.map(Number) },
          ['regionIds'],
          (saved) => {
            const now = new Set(saved.regions.map((region) => String(region.id)));
            return now.size === next.length && next.every((id) => now.has(id));
          },
          (saved) => ({ regions: saved.regions }),
          edit.onSaved,
          (caught) => refusedRegionIds(caught, next),
        )
      }
    />
  );
}

/**
 * The cities a project runs in — a SET, written whole.
 *
 * `InlineRegionsField` is reused despite its company-flavoured name — its
 * contract (label, stored ids, options, whole-array `onSave`) is generic,
 * and copying it would duplicate its portalled-popover leave detection.
 *
 * ⚠️ A set holding a retired city is refused HERE, before the request —
 * reads resolve retired cities fine, but a write containing one is a 422.
 * `activeCityIds` is `null` when the list never arrived, which narrows
 * nothing (absence isn't evidence of retirement); the picker is disabled
 * in that state anyway, so this is belt and braces.
 */
export function ProjectCitiesCell({ row, edit }: CellProps) {
  const stored = useMemo(() => row.cities.map((city) => String(city.id)), [row.cities]);

  /*
   * Active list merged with every city this record already holds, so a chip
   * reads as a name not a bare id. "Retired" hint is gated the same way
   * `pinStored` gates its own — an unread list makes everything look retired.
   */
  const options = useMemo<MultiComboboxOption[]>(() => {
    const merged: MultiComboboxOption[] = [...edit.cities.options];
    const known = new Set(merged.map((option) => option.value));
    const listIsTheActiveMaster = !edit.cities.loading && !edit.cities.failed;
    for (const city of row.cities) {
      const value = String(city.id);
      if (known.has(value)) continue;
      known.add(value);
      merged.push({
        value,
        label: cityLabel(city),
        ...(listIsTheActiveMaster ? { hint: 'Retired' } : {}),
      });
    }
    return merged;
  }, [edit.cities.options, edit.cities.loading, edit.cities.failed, row.cities]);

  const denial = pickerDenial(edit, edit.cities, 'Cities');

  return (
    <InlineRegionsField
      label="Cities"
      values={stored}
      options={options}
      optionsLoading={edit.cities.loading}
      disabled={denial !== null}
      emptyLabel="No cities"
      placeholder="No cities"
      searchPlaceholder="Search cities…"
      {...(denial === null ? {} : { note: denial })}
      display={<CityChips cities={row.cities} />}
      onSave={async (next) => {
        const tooMany = validateProjectCityIds(next);
        if (tooMany) return { status: 'error', message: tooMany };

        const active = edit.activeCityIds;
        if (active) {
          const retired = next.filter((id) => !active.has(id));
          if (retired.length > 0) {
            const names = retired
              .map((id) => options.find((option) => option.value === id)?.label ?? id)
              .join(', ');
            return {
              status: 'error',
              message: `${names} ${retired.length === 1 ? 'is' : 'are'} retired — remove ${retired.length === 1 ? 'it' : 'them'} to save this change.`,
              invalidValues: retired,
            };
          }
        }

        const cityIds = next.map(Number);
        return saveField(
          row,
          // Whole set — `[]` clears it; omitting the key means "leave
          // alone", never done here.
          { cityIds },
          ['cityIds'],
          (saved) => {
            const now = new Set(saved.cities.map((city) => String(city.id)));
            return now.size === next.length && next.every((id) => now.has(id));
          },
          (saved) => ({ cities: saved.cities }),
          edit.onSaved,
        );
      }}
    />
  );
}

const STATUS_OPTIONS: ComboboxOption[] = [
  { value: 'true', label: 'Active' },
  {
    value: 'false',
    label: 'Retired',
    hint: 'Kept out of pickers; still reads on every record naming it',
  },
];

/**
 * Active or retired. `isActive` is `NOT NULL`, so there is no empty row.
 *
 * Retiring is this resource's "remove": assignment checks existence only,
 * never `isActive`, so a retired project keeps resolving on every record
 * that already names it while disappearing from pickers.
 */
export function ProjectStatusCell({ row, edit }: CellProps) {
  return (
    <InlineField
      label="Status"
      kind="picker"
      value={row.isActive ? 'true' : 'false'}
      options={STATUS_OPTIONS}
      searchPlaceholder="Filter…"
      disabled={edit.denial !== null}
      {...(edit.denial === null ? {} : { disabledReason: edit.denial })}
      display={
        <Badge tone={row.isActive ? 'success' : 'neutral'} dot>
          {row.isActive ? 'Active' : 'Retired'}
        </Badge>
      }
      onSave={(next) => {
        const isActive = next === 'true';
        return saveField(
          row,
          { isActive },
          ['isActive'],
          (saved) => saved.isActive === isActive,
          (saved) => ({ isActive: saved.isActive }),
          edit.onSaved,
        );
      }}
    />
  );
}

/**
 * How a product that will NOT be pulled into a new document's line items reads
 * on its chip, and in the cell's `title`.
 *
 * ⚠️ **The MARKED chip is the one that does NOT seed, not the one that does**,
 * and that is the whole reason this is legible: `seeds_line_items` defaults to
 * ON, so most projects mark nothing and a mark means somebody made a decision.
 * Marking the seeding chips instead would put a badge on almost every chip on
 * the page and say nothing at all.
 */
const NOT_SEEDED_MARK = 'not in lines';

/**
 * The idle read view for a project's products.
 *
 * ⚠️ Reads `products`, never the deprecated `product`. There is no empty branch
 * worth designing for — a project always sells at least its primary — so an
 * empty array means the read genuinely came back with none, which is a state the
 * server's own migration guard aborts a deploy over. It is reported as the
 * anomaly it would be rather than rendered as an ordinary blank.
 *
 * ⚠️ **`seedsLineItems` is read straight off the wire, with no default.** It is
 * REQUIRED on `ProjectProductRef`, so a missing one is a contract break and not
 * a state to paper over — and `seedsLineItemsOf` answers for a DRAFT chip that
 * has no stored row, which this view never holds.
 *
 * ⚠️ **The dimming is never the only signal.** The mark is real text, so the
 * fact survives a theme, a screenshot and a reader who cannot tell the two
 * opacities apart — this app's own "never colour as the only signal" rule.
 */
function ProductChips({ products }: { products: Project['products'] }) {
  if (products.length === 0) return <Badge tone="warning">No products</Badge>;
  return (
    <ValuePills
      /*
       * The title carries the mark as well as the name, kept from when the
       * chips collapsed at two. Every product is a pill now (owner, 2026-09-25),
       * so it is a second channel rather than the only one.
       */
      title={products
        .map((product) =>
          product.seedsLineItems ? product.name : `${product.name} (${NOT_SEEDED_MARK})`,
        )
        .join(' · ')}
      items={products.map((product) => ({
        key: product.id,
        muted: !product.seedsLineItems,
        label: product.seedsLineItems ? (
          product.name
        ) : (
          <>
            {product.name}
            <span className="ml-1 text-fg-subtle">· {NOT_SEEDED_MARK}</span>
          </>
        ),
      }))}
    />
  );
}

/**
 * The idle read view for a project's cities.
 *
 * ⚠️ The chip is `city.name` alone; `cityLabel` (the "Bangalore, India"
 * spelling) is used only for the `title`. This split is deliberate: city
 * names collide across countries (Birmingham, England vs. Birmingham,
 * Alabama) and a project has no country of its own, so `city.country` in
 * the title is the only disambiguator. Never move `cityLabel` onto the
 * chips to fix a mismatch — fix the wording instead.
 *
 * Every city is a pill, wrapping (owner, 2026-09-25) — no cap, no "+N more";
 * `cityIds` caps at 200.
 */
function CityChips({ cities }: { cities: Project['cities'] }) {
  // The ordinary case, and not a fault: a project need not name a city.
  if (cities.length === 0) return <span className="text-xs text-fg-subtle">No cities</span>;
  return (
    <ValuePills
      title={cities.map(cityLabel).join(' · ')}
      items={cities.map((city) => ({ key: city.id, label: city.name }))}
    />
  );
}

/** Lucide's `arrow-up-right`, drawn by hand rather than pulling in a dependency. */
function OpenGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <path d="M7 17 17 7" />
      <path d="M7 7h10v10" />
    </svg>
  );
}
